<template>
  <div class="episodes-container">
    <el-form :inline="true" class="inline-form">
      <el-form-item label="完成状态">
        <el-select v-model="episodeSearch.status" style="width: 180px">
          <el-option label="全部" value="all" />
          <el-option label="已完成（全有视频）" value="done" />
          <el-option label="未完成（有缺失视频）" value="todo" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button @click="handleEpisodeSearch">搜索</el-button>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="openEpisodeDialog()">新增分集</el-button>
      </el-form-item>
      <el-form-item v-if="isAdmin">
        <el-button type="warning" icon="el-icon-upload" @click="importDialogVisible = true">JSON 导入</el-button>
      </el-form-item>
    </el-form>

    <el-collapse accordion v-model="activeEpisodeCollapse" @change="handleEpisodeCollapseChange">
      <el-collapse-item
        v-for="episode in filteredEpisodes"
        :key="episode.id"
        :name="String(episode.id)"
      >
        <template slot="title">
          <div class="episode-title-wrap">
            <div class="episode-title-main">
              <span class="episode-title-text">第 {{ episode.episode_index }} 集 - {{ episode.summary }}</span>
              <span class="progress-text">进度：{{ getEpisodeProgressText(episode) }}</span>
              <el-tag 
                v-if="episode.queue_status && episode.queue_status !== 'normal'" 
                size="mini" 
                :type="episode.queue_status === 'generating' ? 'success' : 'warning'"
                style="margin-left: 10px"
              >
                {{ episode.queue_status === 'generating' ? '正在生成' : '队列中' }}
              </el-tag>
            </div>
            <div class="episode-title-actions">
              <el-button 
                v-if="episode.queue_status === 'normal'"
                type="text" 
                @click.stop="handleJoinQueue(episode)"
              >加入队列</el-button>
              <el-button 
                v-else
                type="text" 
                @click.stop="handleRemoveQueue(episode)"
                style="color: #E6A23C"
              >移除队列</el-button>
              <el-button type="text" @click.stop="openEpisodeDialog(episode)">编辑分集</el-button>
              <el-button type="text" @click.stop="removeEpisode(episode.id)" style="color: #F56C6C">删除分集</el-button>
            </div>
          </div>
        </template>
        <div
          v-if="activeEpisodeCollapse === String(episode.id) && episode.summary"
          class="episode-summary-full"
        >
          {{ episode.summary }}
        </div>
        <div class="episode-actions">
          <el-button type="text" @click="handleWatchEpisode(episode)">观看全集</el-button>
          <el-button type="text" @click="openPlanDialog(episode.id)">新增段落</el-button>
        </div>

        <el-table
          v-loading="!!detailLoading[episode.id]"
          :data="(episodeDetails[episode.id] && episodeDetails[episode.id].plans) || []"
          border
          style="width: 100%"
        >
          <el-table-column prop="plan_index" label="顺序" width="70" align="center" />
          <el-table-column prop="name" label="段落名" width="160" show-overflow-tooltip />
          <el-table-column prop="duration" label="时长(秒)" width="90" align="center" />
          <el-table-column prop="scene_relation" label="场景关系" width="120" show-overflow-tooltip />
          <el-table-column prop="prompt" label="分段提示词" min-width="320" class-name="col-plan-prompt">
            <template slot-scope="scope">
              <div class="plan-prompt-full">{{ scope.row.prompt || "—" }}</div>
            </template>
          </el-table-column>
          <el-table-column label="对应视频" width="260">
            <template slot-scope="scope">
              <video
                v-if="scope.row.video_url"
                :src="resolveMediaUrl(scope.row.video_url)"
                controls
                class="video-preview"
              />
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="280">
            <template slot-scope="scope">
              <el-button type="text" @click="openPlanDialog(episode.id, scope.row)">
                编辑
              </el-button>
              <el-button
                type="text"
                @click="handleViewPlanAssets(scope.row.id)"
              >
                查看资源
              </el-button>
              <el-button
                type="text"
                :loading="downloadingPlanId === scope.row.id"
                :disabled="downloadingPlanId !== null && downloadingPlanId !== scope.row.id"
                @click="openPackageDownloadDialog(scope.row)"
              >
                下载资源包
              </el-button>
              <el-button
                v-if="scope.row.video_url"
                type="text"
                @click="handleDownloadVideoRef(scope.row.video_url)"
              >
                下载视频
              </el-button>
              <el-button
                type="text"
                @click="handleGoToGenerateRef(episode, scope.row)"
              >
                去生成
              </el-button>
              <el-button type="text" @click="removePlan(episode.id, scope.row.id)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-collapse-item>
    </el-collapse>

    <!-- Dialogs -->
    <!-- Episode Form -->
    <el-dialog
      :title="episodeForm.id ? '编辑分集' : '新增分集'"
      :visible.sync="episodeDialogVisible"
      width="720px"
    >
      <el-form label-width="90px">
        <el-form-item label="第几集">
          <el-input-number v-model="episodeForm.episode_index" :min="1" />
        </el-form-item>
        <el-form-item label="剧情摘要">
          <el-input v-model="episodeForm.summary" type="textarea" :rows="6" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="episodeDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveEpisode">保存</el-button>
      </div>
    </el-dialog>

    <!-- Plan Form -->
    <el-dialog
      :title="planForm.id ? '编辑段落' : '新增段落'"
      :visible.sync="planDialogVisible"
      width="760px"
    >
      <el-form label-width="90px">
        <el-form-item label="段落顺序">
          <el-input-number v-model="planForm.plan_index" :min="1" />
        </el-form-item>
        <el-form-item label="段落名">
          <el-input v-model="planForm.name" placeholder="段落名称" />
        </el-form-item>
        <el-form-item label="时长(秒)">
          <el-input-number v-model="planForm.duration" :min="1" />
        </el-form-item>
        <el-form-item label="场景关系">
          <el-input v-model="planForm.scene_relation" placeholder="如：场景切换 / 延续上一段" />
        </el-form-item>
        <el-form-item label="分段提示词">
          <div class="mention-wrapper">
            <el-input
              ref="planPromptInput"
              v-model="planForm.prompt"
              type="textarea"
              :rows="7"
              @input="handlePromptInput"
              @keydown.native="handlePromptKeyDown"
            />
            <div v-if="mentionVisible" class="mention-list" :style="{ top: mentionPosition.top + 'px', left: mentionPosition.left + 'px' }">
              <div
                v-for="(asset, index) in filteredMentionAssets"
                :key="asset.id"
                class="mention-item"
                :class="{ active: index === mentionIndex }"
                @click="selectMention(asset)"
              >
                <span class="mention-item-type">{{ asset.asset_type }}</span>
                <span class="mention-item-name">{{ asset.name }}</span>
              </div>
              <div v-if="!filteredMentionAssets.length" class="mention-empty">无匹配资产</div>
            </div>
            <!-- Mirror div for position calculation -->
            <div ref="mentionMirror" class="mention-mirror"></div>
          </div>
        </el-form-item>
        <el-form-item label="对应视频">
          <el-button size="mini" :disabled="!planForm.video_url" @click="openVideoPreview(planForm.video_url)">
            播放预览
          </el-button>
          <span v-if="!planForm.video_url" class="media-empty-text">暂无视频</span>
          <el-upload
            ref="planVideoUpload"
            :show-file-list="false"
            :http-request="handlePlanVideoUpload"
            action="#"
            accept="video/*"
            class="upload-inline"
          >
            <el-button size="mini" :loading="planVideoUploading">
              {{ planVideoUploading ? `上传中 ${planVideoUploadProgress}%` : "上传本地视频" }}
            </el-button>
          </el-upload>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="planDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="savePlan">保存</el-button>
      </div>
    </el-dialog>

    <!-- Plan Assets -->
    <el-dialog title="查看对应资源" :visible.sync="planAssetsDialogVisible" width="600px">
      <el-table :data="planMatchedAssets" border>
        <el-table-column prop="name" label="资源名" />
        <el-table-column prop="asset_type" label="类型" width="100" />
        <el-table-column label="图片" width="160">
          <template slot-scope="scope">
            <el-image
              v-if="scope.row.image_url"
              :src="resolveMediaUrl(scope.row.image_url)"
              style="width: 120px; height: 80px"
              fit="cover"
              :preview-src-list="[resolveMediaUrl(scope.row.image_url)]"
            />
            <span v-else>无图片</span>
          </template>
        </el-table-column>
      </el-table>
      <div slot="footer">
        <el-button @click="planAssetsDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>

    <!-- Watch Episode -->
    <el-dialog title="全集预览（顺序播放）" :visible.sync="episodeWatchDialogVisible" width="900px" top="5vh" @closed="onWatchDialogClosed">
      <div v-if="currentWatchPlans.length" class="watch-dialog-content">
        <div class="video-container">
          <div class="current-info" v-if="currentWatchPlans[currentPlayIndex]">
            正在播放 第 {{ currentWatchPlans[currentPlayIndex].plan_index }} 段：{{ currentWatchPlans[currentPlayIndex].name }}
          </div>
          <video
            ref="episodeVideo"
            v-if="currentWatchPlans[currentPlayIndex] && currentWatchPlans[currentPlayIndex].video_url"
            :key="currentWatchPlans[currentPlayIndex].video_url"
            :src="resolveMediaUrl(currentWatchPlans[currentPlayIndex].video_url)"
            controls
            autoplay
            preload="auto"
            class="full-video-player"
            @ended="handleVideoEnded"
          />
          <div v-else class="video-placeholder">
            当前段落无视频内容
          </div>
        </div>
        <div class="segment-navigator">
          <div
            v-for="(plan, idx) in currentWatchPlans"
            :key="plan.id"
            class="segment-tag"
            :class="{
              'is-active': currentPlayIndex === idx,
              'has-video': !!plan.video_url,
              'no-video': !plan.video_url
            }"
            @click="playSegment(idx)"
          >
            {{ plan.plan_index }}
          </div>
        </div>
      </div>
      <div v-else class="media-empty-text">暂无段落内容</div>
    </el-dialog>

    <!-- General Previews -->
    <el-dialog title="视频预览" :visible.sync="videoPreviewDialogVisible" width="760px" @closed="closeVideoPreview" append-to-body>
      <video v-if="videoPreviewDialogVisible && videoPreviewUrl" :src="videoPreviewUrl" controls autoplay class="video-preview-dialog" />
    </el-dialog>

    <el-dialog
      title="下载资源包"
      :visible.sync="packageDialogVisible"
      width="920px"
      top="6vh"
      custom-class="package-download-dialog"
      @closed="onPackageDialogClosed"
    >
      <div v-loading="packagePreviewLoading" class="package-download-body">
        <div class="package-download-left">
          <div class="package-download-col-title">图片顺序（与文案中的 @ 引用一致）</div>
          <div v-if="!packageImageSlots.length" class="package-empty">暂无资源引用，编辑右侧文案可添加 <code>@资源名</code> 引用</div>
          <div
            v-for="(slot, si) in packageImageSlots"
            :key="'ps-' + si + '-' + (slot.asset && slot.asset.id ? slot.asset.id : slot.kind) + '-' + slot.image_index"
            class="package-slot"
          >
            <div class="package-slot-head">
              <span class="package-slot-idx">图片{{ slot.image_index }}</span>
              <el-button
                type="text"
                class="package-slot-del"
                @click="removePackageImageSlot(slot)"
              >删除</el-button>
            </div>
            <div v-if="slot.kind === 'prev_video_tail'" class="package-slot-card package-slot-prev">
              <template v-if="!slot.ok">
                <i class="el-icon-warning package-slot-warn-icon" />
                <span class="package-slot-warn-text">{{ slot.error || "上一段尾帧不可用" }}</span>
              </template>
              <template v-else>
                <i class="el-icon-video-camera package-prev-icon" />
                <span>{{ slot.label }}</span>
              </template>
            </div>
            <div v-else class="package-slot-card">
              <el-image
                v-if="slot.asset && slot.asset.image_url"
                :src="resolveMediaUrl(slot.asset.image_url)"
                class="package-slot-img"
                fit="cover"
                :preview-src-list="[resolveMediaUrl(slot.asset.image_url)]"
              />
              <div v-else class="package-no-img">无图片</div>
              <div class="package-slot-cap">
                {{ (slot.asset && slot.asset.name) || slot.label }}
                <span v-if="slot.kind === 'asset' && !slot.has_image" class="package-missing">（缺图，打包时保留 @ 名）</span>
              </div>
            </div>
          </div>
        </div>
        <div class="package-download-right">
          <div class="package-download-col-title">文案</div>
          <p class="package-download-hint">可点击左侧某张的「删除」从本包去掉该图（会同步改文案，或针对尾帧只影响本次打包）。下载按当前文案与列表生成 <code>content.txt</code> 与图片文件；底部可选择 provider，KlingAI 模式下文案中的 <code>图片N</code> 会转为 <code>&lt;&lt;&lt;image_N&gt;&gt;&gt;</code> 格式。</p>
          <el-input
            v-model="packagePrompt"
            type="textarea"
            :rows="16"
            placeholder="分段提示词"
            @input="onPackagePromptInput"
          />
        </div>
      </div>
      <div slot="footer">
        <el-select v-model="packageProvider" size="small" style="margin-right: 12px;" @change="onPackageProviderChange">
          <el-option label="通用 (图片N)" value="comfyui" />
          <el-option label="Seedance (图片N)" value="seedance" />
          <el-option label="KlingAI (<<<image_N>>>)" value="klingai" />
        </el-select>
        <el-button @click="packageDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          :loading="!!packageTargetPlan && downloadingPlanId === packageTargetPlan.id"
          :disabled="!packageTargetPlan"
          @click="handleDownloadFromPackageDialog"
        >
          下载
        </el-button>
      </div>
    </el-dialog>

    <!-- JSON Import Dialog -->
    <el-dialog title="JSON 导入（增量补充）" :visible.sync="importDialogVisible" width="800px" append-to-body>
      <div style="margin-bottom: 10px; color: #666; font-size: 13px;">
        请粘贴符合 <code>djson.md</code> 格式的 JSON 内容。此次导入将：
        <ul style="margin: 5px 0 0 18px; padding: 0;">
          <li><b>资产合并</b>：按名称和类型匹配，已存在的资产会更新 Prompt，不存在的则新增。</li>
          <li><b>分集追加</b>：忽略 JSON 中的集数，将所有分集依次追加到当前故事的最后一集之后。</li>
        </ul>
      </div>
      <el-input
        v-model="importJsonText"
        type="textarea"
        :rows="20"
        placeholder="在此粘贴 JSON 内容..."
      />
      <div slot="footer">
        <el-button @click="importDialogVisible = false">取消</el-button>
        <el-button type="warning" :loading="importing" @click="handleImportJson">确认并开始追加</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getEpisodes,
  getEpisodeDetail,
  createEpisode,
  updateEpisode,
  deleteEpisode,
  createPlan,
  updatePlan,
  deletePlan,
  downloadPlanPackage,
  postPlanPackagePreview,
  getPlanAssets,
  getPlanGenMetadata,
  uploadVideo,
  joinEpisodeQueue,
  removeEpisodeQueue,
  importAppendJson,
  getAssets
} from "@/net/api";
import { resolveMediaUrl, handleDownloadVideo } from "@/utils/media";

const defaultEpisode = () => ({
  id: null,
  episode_index: 1,
  summary: ""
});

const defaultPlan = () => ({
  id: null,
  plan_index: 1,
  name: "",
  duration: 30,
  scene_relation: "",
  prompt: "",
  video_url: ""
});

export default {
  name: "Episodes",
  data() {
    return {
      episodes: [],
      episodeDetails: {},
      detailLoading: {},
      episodeSearch: {
        status: "all"
      },
      activeEpisodeCollapse: "",
      episodeForm: defaultEpisode(),
      planForm: defaultPlan(),
      currentPlanEpisodeId: null,
      episodeDialogVisible: false,
      planDialogVisible: false,
      planAssetsDialogVisible: false,
      planMatchedAssets: [],
      episodeWatchDialogVisible: false,
      currentWatchPlans: [],
      currentPlayIndex: 0,
      videoPreviewDialogVisible: false,
      videoPreviewUrl: "",
      downloadingPlanId: null,
      planVideoUploading: false,
      planVideoUploadProgress: 0,
      packageDialogVisible: false,
      packageTargetPlan: null,
      packagePrompt: "",
      packageImageSlots: [],
      packagePreviewLoading: false,
      packagePreviewDebounce: null,
      /** 是否包含「上一段视频尾帧」为图片1；点左侧删除尾帧会置为 false，仅本弹窗/本次下载生效 */
      packageIncludePrevVideoTail: true,
      /** 下载资源包时选择的 provider，影响 content.txt 中图片引用格式 */
      packageProvider: "comfyui",
      episodeTimer: null,
      importDialogVisible: false,
      importJsonText: "",
      importing: false,
      // Mention feature state
      allAssets: [],
      mentionVisible: false,
      mentionQuery: "",
      mentionIndex: 0,
      mentionPosition: { top: 0, left: 0 }
    };
  },
  watch: {
    currentPlayIndex() {
      this.$nextTick(() => {
        const video = this.$refs.episodeVideo;
        if (video) {
          video.load();
          video.play().catch((err) => {
            console.warn("Auto-play failed:", err);
          });
        }
      });
    }
  },
  computed: {
    filteredEpisodes() {
      return this.episodes.filter((episode) => {
        if (this.episodeSearch.status === "all") {
          return true;
        }
        const progress = this.getEpisodeProgress(episode);
        const done = progress.missing === 0 && progress.total > 0;
        return this.episodeSearch.status === "done" ? done : !done;
      });
    },
    isAdmin() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        return user.role === "admin";
      } catch (e) {
        return false;
      }
    },
    filteredMentionAssets() {
      if (!this.mentionQuery) return this.allAssets.slice(0, 20);
      const q = this.mentionQuery.toLowerCase();
      return this.allAssets.filter(a => 
        (a.name || "").toLowerCase().includes(q) || 
        (a.asset_type || "").toLowerCase().includes(q)
      ).slice(0, 20);
    }
  },
  created() {
    this.loadEpisodes();
    this.startEpisodePolling();
    this.loadAllAssets();
  },
  beforeDestroy() {
    this.stopEpisodePolling();
  },
  methods: {
    resolveMediaUrl,
    async loadEpisodes() {
      try {
        const { data } = await getEpisodes();
        this.episodes = data;
      } catch (e) {
        this.$message.error("加载列表失败");
      }
    },
    async loadEpisodeDetail(episodeId) {
      this.$set(this.detailLoading, episodeId, true);
      try {
        const { data } = await getEpisodeDetail(episodeId);
        this.$set(this.episodeDetails, episodeId, data);
      } catch (e) {
        console.error(e);
      } finally {
        this.$set(this.detailLoading, episodeId, false);
      }
    },
    handleEpisodeCollapseChange(activeId) {
      if (activeId) {
        this.loadEpisodeDetail(activeId);
      }
    },
    handleEpisodeSearch() {
      this.loadEpisodes();
    },
    openEpisodeDialog(row) {
      if (row) {
        this.episodeForm = { ...row };
      } else {
        const maxIndex = this.episodes.length > 0 
          ? Math.max(...this.episodes.map(e => e.episode_index || 0)) 
          : 0;
        this.episodeForm = { ...defaultEpisode(), episode_index: maxIndex + 1 };
      }
      this.episodeDialogVisible = true;
    },
    async saveEpisode() {
      try {
        if (this.episodeForm.id) {
          await updateEpisode(this.episodeForm.id, this.episodeForm);
        } else {
          await createEpisode(this.episodeForm);
        }
        this.episodeDialogVisible = false;
        this.loadEpisodes();
        this.$message.success("保存成功");
      } catch (e) {
        this.$message.error("保存失败");
      }
    },
    async removeEpisode(id) {
      try {
        await this.$confirm("确定删除该分集吗？", "提示", { type: "warning" });
        await deleteEpisode(id);
        this.loadEpisodes();
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    },
    openPlanDialog(episodeId, plan) {
      this.currentPlanEpisodeId = episodeId;
      if (plan) {
        this.planForm = { ...plan };
      } else {
        const episodeData = this.episodeDetails[episodeId];
        const maxIndex = (episodeData && episodeData.plans && episodeData.plans.length > 0)
          ? Math.max(...episodeData.plans.map(p => p.plan_index || 0))
          : 0;
        this.planForm = { ...defaultPlan(), plan_index: maxIndex + 1 };
      }
      this.mentionVisible = false;
      this.planDialogVisible = true;
    },
    async savePlan() {
      try {
        if (this.planForm.id) {
          await updatePlan(this.planForm.id, this.planForm);
        } else {
          await createPlan(this.currentPlanEpisodeId, this.planForm);
        }
        this.planDialogVisible = false;
        this.loadEpisodeDetail(this.currentPlanEpisodeId);
        this.$message.success("保存成功");
      } catch (e) {
        this.$message.error("保存失败");
      }
    },
    async removePlan(episodeId, planId) {
      try {
        await this.$confirm("确定删除该段落吗？", "提示", { type: "warning" });
        await deletePlan(planId);
        this.loadEpisodeDetail(episodeId);
      } catch (e) {
        if (e !== "cancel") this.$message.error("删除失败");
      }
    },
    async handleViewPlanAssets(planId) {
      try {
        const { data } = await getPlanAssets(planId);
        this.planMatchedAssets = data;
        this.planAssetsDialogVisible = true;
      } catch (e) {
        this.$message.error("获取资源失败");
      }
    },
    getEpisodeProgress(episode) {
      if (this.episodeDetails[episode.id] && this.episodeDetails[episode.id].plans) {
        const plans = this.episodeDetails[episode.id].plans;
        if (!plans.length) return { total: 0, done: 0 };
        const done = plans.filter((p) => !!p.video_url).length;
        return { total: plans.length, done };
      }
      return {
        total: episode.total_count || 0,
        done: episode.done_count || 0
      };
    },
    getEpisodeProgressText(episode) {
      const p = this.getEpisodeProgress(episode);
      if (p.total === 0) return "无段落";
      return `${p.done} / ${p.total}`;
    },
    openPackageDownloadDialog(plan) {
      this.packageTargetPlan = plan;
      this.packagePrompt = plan.prompt || "";
      this.packageIncludePrevVideoTail = true;
      this.packageProvider = "comfyui";
      this.packageDialogVisible = true;
      this.$nextTick(() => {
        this.fetchPackagePreview();
      });
    },
    onPackageDialogClosed() {
      if (this.packagePreviewDebounce) {
        clearTimeout(this.packagePreviewDebounce);
        this.packagePreviewDebounce = null;
      }
      this.packageTargetPlan = null;
      this.packageImageSlots = [];
      this.packagePrompt = "";
      this.packageIncludePrevVideoTail = true;
      this.packageProvider = "comfyui";
    },
    removePackageImageSlot(slot) {
      if (!slot) {
        return;
      }
      if (slot.kind === "prev_video_tail") {
        this.packageIncludePrevVideoTail = false;
        this.fetchPackagePreview();
        return;
      }
      if (slot.kind === "asset" && slot.asset) {
        const name = (slot.asset.name || "").trim();
        if (!name) {
          return;
        }
        const tag = `@${name}`;
        this.packagePrompt = (this.packagePrompt || "").split(tag).join("");
        this.fetchPackagePreview();
      }
    },
    onPackagePromptInput() {
      if (this.packagePreviewDebounce) {
        clearTimeout(this.packagePreviewDebounce);
      }
      this.packagePreviewDebounce = setTimeout(() => {
        this.fetchPackagePreview();
      }, 350);
    },
    fetchPackagePreview() {
      if (!this.packageTargetPlan) {
        return;
      }
      this.packagePreviewLoading = true;
      postPlanPackagePreview(this.packageTargetPlan.id, {
        prompt: this.packagePrompt,
        include_prev_video_tail: this.packageIncludePrevVideoTail,
        provider: this.packageProvider
      })
        .then(({ data }) => {
          this.packageImageSlots = (data && data.image_slots) || [];
        })
        .catch(() => {
          this.$message.error("预览资源列表失败");
        })
        .finally(() => {
          this.packagePreviewLoading = false;
        });
    },
    async handleDownloadFromPackageDialog() {
      if (!this.packageTargetPlan) {
        return;
      }
      await this.handleDownloadPlanPackage(this.packageTargetPlan, {
        prompt: this.packagePrompt,
        provider: this.packageProvider
      });
    },
    onPackageProviderChange() {
      // provider 切换时重新请求 preview，后端可能返回不同格式的 prompt
      this.fetchPackagePreview();
    },
    /**
     * @param {Object} plan
     * @param {boolean|{force?: boolean, prompt?: string, provider?: string}} options 兼容第二参数为 boolean
     */
    async handleDownloadPlanPackage(plan, options) {
      let force = false;
      let promptForRequest;
      let providerForRequest;
      if (typeof options === "boolean") {
        force = options;
      } else if (options && typeof options === "object") {
        force = !!options.force;
        if (options.prompt !== undefined) {
          promptForRequest = options.prompt;
        }
        if (options.provider !== undefined) {
          providerForRequest = options.provider;
        }
      }
      if (promptForRequest === undefined && this.packageDialogVisible) {
        promptForRequest = this.packagePrompt;
      }
      if (providerForRequest === undefined && this.packageDialogVisible) {
        providerForRequest = this.packageProvider;
      }
      if (this.downloadingPlanId && !force) {
        return;
      }
      this.downloadingPlanId = plan.id;
      try {
        const req = (() => {
          if (promptForRequest === undefined) {
            const params = { force };
            if (providerForRequest !== undefined) {
              params.provider = providerForRequest;
            }
            return downloadPlanPackage(plan.id, params);
          }
          const postPayload = { force, prompt: promptForRequest };
          if (this.packageDialogVisible) {
            postPayload.include_prev_video_tail = this.packageIncludePrevVideoTail;
          }
          if (providerForRequest !== undefined) {
            postPayload.provider = providerForRequest;
          }
          return downloadPlanPackage(plan.id, postPayload);
        })();
        const { data } = await req;
        const rel = (data && data.url) || "";
        if (!rel) {
          this.$message.error("服务器未返回下载地址");
          this.downloadingPlanId = null;
          return;
        }
        const href = rel.startsWith("http") ? rel : `${window.location.origin}${rel}`;
        window.open(href, "_blank");
        this.$message.success((data && data.message) || "已开始下载");
        this.downloadingPlanId = null;
        if (this.packageDialogVisible) {
          this.packageDialogVisible = false;
        }
      } catch (error) {
        const res = error.response;
        if (!res || !res.data) {
          this.$message.error("下载失败");
          this.downloadingPlanId = null;
          return;
        }
        const errData = res.data;
        if (errData instanceof Blob) {
          const reader = new FileReader();
          reader.onload = async () => {
            try {
              const parsed = JSON.parse(reader.result);
              await this._handleDownloadPlanPackageError(plan, parsed, promptForRequest);
            } catch (e) {
              this.$message.error("下载失败");
              this.downloadingPlanId = null;
            }
          };
          reader.readAsText(errData);
          return;
        }
        await this._handleDownloadPlanPackageError(plan, errData, promptForRequest);
      }
    },
    async _handleDownloadPlanPackageError(plan, errData, promptForRequest) {
      if (errData && errData.error_code === "MISSING_IMAGES") {
        const missingList = (errData.missing_assets || [])
          .map((a) => `• ${a}`)
          .join("\n");
        try {
          await this.$confirm(
            `检测到以下资源缺失图片：\n\n${missingList}\n\n确认要强制下载资源包吗？（缺失资源的文案将保持原样，不会被替换为“图片N”）`,
            "提示",
            {
              confirmButtonText: "强制下载",
              cancelButtonText: "取消",
              type: "warning"
            }
          );
          const usePrompt =
            promptForRequest !== undefined
              ? promptForRequest
              : this.packageDialogVisible
                ? this.packagePrompt
                : undefined;
          if (usePrompt !== undefined) {
            await this.handleDownloadPlanPackage(plan, { force: true, prompt: usePrompt });
          } else {
            await this.handleDownloadPlanPackage(plan, true);
          }
        } catch (e) {
          // cancel
        }
        this.downloadingPlanId = null;
        return;
      }
      this.$message.error((errData && errData.message) || "下载失败");
      this.downloadingPlanId = null;
    },
    handleDownloadVideoRef(url) {
      handleDownloadVideo(url);
    },
    async handleGoToGenerateRef(episode, plan) {
      try {
        const res = await getPlanGenMetadata(plan.id);
        const { prompt, reference_images } = res.data;
        this.$router.push({
          path: "/image-tasks",
          query: {
            action: "generate",
            type: "plan",
            id: plan.id,
            episode_index: episode.episode_index,
            plan_index: plan.plan_index,
            prompt: prompt,
            duration: plan.duration,
            reference_images: JSON.stringify(reference_images)
          }
        });
      } catch (e) {
        this.$message.error("获取素材信息失败");
      }
    },
    handleWatchEpisode(episode) {
      const plans = (this.episodeDetails[episode.id] && this.episodeDetails[episode.id].plans) || [];
      this.currentWatchPlans = [...plans].sort((a, b) => a.plan_index - b.plan_index);
      if (!this.currentWatchPlans.length) {
        this.$message.info("该分集暂无段落");
        return;
      }
      this.currentPlayIndex = 0;
      this.episodeWatchDialogVisible = true;
    },
    handleVideoEnded() {
      if (this.currentPlayIndex < this.currentWatchPlans.length - 1) {
        this.currentPlayIndex++;
      }
    },
    playSegment(idx) {
      this.currentPlayIndex = idx;
    },
    openVideoPreview(url) {
      this.videoPreviewUrl = resolveMediaUrl(url);
      this.videoPreviewDialogVisible = true;
    },
    onWatchDialogClosed() {
      const video = this.$refs.episodeVideo;
      if (video) {
        video.pause();
      }
      this.currentPlayIndex = 0;
      this.currentWatchPlans = [];
    },
    closeVideoPreview() {
      this.videoPreviewDialogVisible = false;
      this.videoPreviewUrl = "";
    },
    async handlePlanVideoUpload(option) {
      this.planVideoUploading = true;
      this.planVideoUploadProgress = 0;
      try {
        const { data } = await uploadVideo(option.file, (p) => {
          this.planVideoUploadProgress = p;
        });
        this.planForm.video_url = data.url;
        this.$message.success("上传成功");
      } catch (e) {
        this.$message.error("上传失败");
      } finally {
        this.planVideoUploading = false;
      }
    },
    startEpisodePolling() {
      this.episodeTimer = setInterval(() => {
        const needRefresh = this.episodes.some(e => e.queue_status === 'generating' || e.queue_status === 'queued');
        if (needRefresh) {
          this.loadEpisodes();
        }
      }, 5000);
    },
    stopEpisodePolling() {
      if (this.episodeTimer) clearInterval(this.episodeTimer);
    },
    async handleJoinQueue(episode) {
      try {
        await joinEpisodeQueue(episode.id);
        this.$message.success("已加入生成队列");
        this.loadEpisodes();
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        this.$message.error(msg || "加入队列失败");
      }
    },
    async handleRemoveQueue(episode) {
      try {
        await removeEpisodeQueue(episode.id);
        this.$message.success("已移除队列");
        this.loadEpisodes();
      } catch (e) {
        this.$message.error("操作失败");
      }
    },
    async handleImportJson() {
      if (!this.importJsonText.trim()) {
        this.$message.warning("请输入 JSON 内容");
        return;
      }
      let payload;
      try {
        payload = JSON.parse(this.importJsonText);
      } catch (e) {
        this.$message.error("JSON 格式错误，请检查");
        return;
      }

      this.importing = true;
      try {
        await importAppendJson(payload);
        this.$message.success("导入并追加成功");
        this.importDialogVisible = false;
        this.importJsonText = "";
        this.loadEpisodes();
        this.activeEpisodeCollapse = "";
        this.episodeDetails = {};
      } catch (e) {
        const msg = e.response?.data?.message || "导入失败";
        this.$message.error(msg);
      } finally {
        this.importing = false;
      }
    },
    async loadAllAssets() {
      try {
        const { data } = await getAssets();
        this.allAssets = data;
      } catch (e) {
        console.error("Failed to load assets for mentions", e);
      }
    },
    handlePromptInput() {
      this.$nextTick(() => {
        const input = this.$refs.planPromptInput.$refs.textarea;
        const selectionStart = input.selectionStart;
        const textBefore = this.planForm.prompt.substring(0, selectionStart);
        
        const lastAtIndex = textBefore.lastIndexOf("@");
        if (lastAtIndex !== -1) {
          const query = textBefore.substring(lastAtIndex + 1);
          // Only show if no space between @ and cursor
          if (!query.includes(" ") && !query.includes("\n")) {
            this.mentionQuery = query;
            this.mentionVisible = true;
            this.mentionIndex = 0;
            this.calculateMentionPosition(input, lastAtIndex);
            return;
          }
        }
        this.mentionVisible = false;
      });
    },
    calculateMentionPosition(input, index) {
      const mirror = this.$refs.mentionMirror;
      if (!mirror) return;
      const styles = window.getComputedStyle(input);
      
      // Copy styles to mirror
      mirror.style.width = styles.width;
      mirror.style.padding = styles.padding;
      mirror.style.fontSize = styles.fontSize;
      mirror.style.fontFamily = styles.fontFamily;
      mirror.style.lineHeight = styles.lineHeight;
      mirror.style.border = styles.border;
      
      const textBefore = this.planForm.prompt.substring(0, index);
      mirror.textContent = textBefore;
      
      const marker = document.createElement("span");
      marker.textContent = "@";
      mirror.appendChild(marker);
      
      // Rough estimation of position within the wrapper
      // Since wrapper is relative, we need offset from top-left of textarea
      const lineHeight = parseInt(styles.lineHeight) || 20;
      this.mentionPosition = {
        top: marker.offsetTop + lineHeight + 2,
        left: Math.min(marker.offsetLeft, parseInt(styles.width) - 160)
      };
    },
    handlePromptKeyDown(e) {
      if (!this.mentionVisible) return;
      
      const list = this.filteredMentionAssets;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.mentionIndex = (this.mentionIndex + 1) % list.length;
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this.mentionIndex = (this.mentionIndex - 1 + list.length) % list.length;
      } else if (e.key === "Enter" || e.key === "Tab") {
        if (list.length > 0) {
          e.preventDefault();
          this.selectMention(list[this.mentionIndex]);
        } else {
          this.mentionVisible = false;
        }
      } else if (e.key === "Escape") {
        this.mentionVisible = false;
      }
    },
    selectMention(asset) {
      const input = this.$refs.planPromptInput.$refs.textarea;
      const selectionStart = input.selectionStart;
      const textBefore = this.planForm.prompt.substring(0, selectionStart);
      const textAfter = this.planForm.prompt.substring(selectionStart);
      
      const lastAtIndex = textBefore.lastIndexOf("@");
      const newTextBefore = textBefore.substring(0, lastAtIndex) + "@" + asset.name + " ";
      
      this.planForm.prompt = newTextBefore + textAfter;
      this.mentionVisible = false;
      
      this.$nextTick(() => {
        input.focus();
        const newPos = newTextBefore.length;
        input.setSelectionRange(newPos, newPos);
      });
    }
  }
};
</script>

<style scoped>
.episode-title-wrap {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding-right: 20px;
  box-sizing: border-box;
}
.episode-title-main {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}
.episode-title-text {
  flex: 1;
  margin-right: 20px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.episode-title-actions {
  flex-shrink: 0;
  margin-left: 20px;
  display: flex;
  align-items: center;
}
.episode-title-actions .el-button {
  padding: 0 5px;
}
.episode-summary-full {
  padding: 10px 12px;
  margin-bottom: 12px;
  background: #f4f6f8;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.55;
  color: #303133;
  white-space: pre-wrap;
  word-break: break-word;
}
.plan-prompt-full {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.55;
  text-align: left;
}
::v-deep .col-plan-prompt .cell {
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
}
.progress-text {
  flex-shrink: 0;
  color: #909399;
  font-size: 13px;
}
.episode-actions {
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f8f9fa;
  border-radius: 4px;
}
.video-preview {
  width: 100%;
  max-width: 200px;
  border-radius: 4px;
}
.watch-dialog-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.video-container {
  background: #000;
  height: 520px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
}
.current-info {
  color: #fff;
  padding: 8px 15px;
  background: rgba(0,0,0,0.6);
  font-size: 14px;
  z-index: 10;
}
.full-video-player {
  flex: 1;
  width: 100%;
  min-height: 0;
  object-fit: contain;
  outline: none;
}
.video-placeholder {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
}
.segment-navigator {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}
.segment-tag {
  padding: 6px 14px;
  border: 1px solid #dcdfe6;
  cursor: pointer;
  border-radius: 4px;
  font-size: 12px;
  transition: all 0.3s;
}
.segment-tag:hover {
  border-color: #409eff;
  color: #409eff;
}
.segment-tag.is-active {
  background: #409eff;
  color: #fff;
  border-color: #409eff;
}
.segment-tag.has-video {
  border-color: #67c23a;
  color: #67c23a;
}
.segment-tag.has-video.is-active {
  background: #67c23a;
  color: #fff;
}
.segment-tag.no-video {
  border-color: #f56c6c;
  color: #f56c6c;
}
.segment-tag.no-video.is-active {
  background: #f56c6c;
  color: #fff;
}
.video-preview-dialog {
  width: 100%;
  border-radius: 8px;
}
.media-empty-text {
  color: #909399;
  font-size: 13px;
}
.package-download-body {
  display: flex;
  gap: 20px;
  min-height: 360px;
  align-items: flex-start;
}
.package-download-left {
  width: 280px;
  flex-shrink: 0;
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 4px;
}
.package-download-right {
  flex: 1;
  min-width: 0;
}
.package-download-col-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #303133;
}
.package-download-hint {
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
  margin: 0 0 10px;
}
.package-empty {
  font-size: 13px;
  color: #909399;
  padding: 12px 0;
  line-height: 1.5;
}
.package-slot {
  margin-bottom: 12px;
}
.package-slot-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.package-slot-idx {
  font-size: 12px;
  color: #606266;
}
.package-slot-del {
  flex-shrink: 0;
  padding: 0 4px;
  font-size: 12px;
}
.package-slot-card {
  border: 1px solid #ebeef5;
  border-radius: 6px;
  overflow: hidden;
  background: #fafafa;
}
.package-slot-img {
  display: block;
  width: 100%;
  height: 120px;
}
.package-slot-cap {
  font-size: 12px;
  padding: 6px 8px;
  line-height: 1.4;
  word-break: break-all;
  color: #303133;
}
.package-no-img {
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c0c4cc;
  font-size: 13px;
  background: #f5f7fa;
}
.package-slot-prev {
  padding: 12px 10px;
  min-height: 80px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  font-size: 13px;
  color: #606266;
}
.package-prev-icon {
  font-size: 28px;
  color: #409eff;
}
.package-slot-warn-icon {
  color: #e6a23c;
  font-size: 18px;
}
.package-slot-warn-text {
  font-size: 12px;
  color: #e6a23c;
  line-height: 1.4;
}
.package-missing {
  color: #e6a23c;
  font-weight: normal;
}

/* Mention Styles */
.mention-wrapper {
  position: relative;
}
.mention-list {
  position: absolute;
  z-index: 3000;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.1);
  max-height: 200px;
  overflow-y: auto;
  min-width: 160px;
}
.mention-item {
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  transition: background 0.2s;
}
.mention-item:hover, .mention-item.active {
  background-color: #f5f7fa;
  color: #409eff;
}
.mention-item-type {
  font-size: 11px;
  padding: 1px 4px;
  background: #eee;
  border-radius: 3px;
  color: #999;
}
.mention-item-name {
  font-weight: 500;
}
.mention-empty {
  padding: 12px;
  color: #999;
  font-size: 12px;
  text-align: center;
}
.mention-mirror {
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: inherit;
  font-size: inherit;
  line-height: inherit;
  padding: inherit;
  border: 1px solid transparent;
  box-sizing: border-box;
}
</style>
